import api from '@/lib/axios';

export interface StoreNote {
  content: string;
  createdAt: string;
  createdBy: string;
}

export interface FollowUp {
  _id: string;
  title: string;
  description?: string;
  dueDate: string;
  priority: 'low' | 'medium' | 'high';
  completed: boolean;
  completedAt?: string;
  completedBy?: string;
  createdAt: string;
  createdBy: string;
}

export interface Store {
  _id: string;
  storeNumber: string;
  name: string;
  storeAddress: string;
  storePhone?: string;
  storeEmail?: string;
  billingMethod?: 'stripe' | 'check' | 'comp';
  notes?: StoreNote[];
  followUps?: FollowUp[];
  createdAt: string;
  userCount?: number;
  status?: 'active' | 'inactive';
  subscription?: {
    status: 'active' | 'expired' | 'trial' | 'none';
    features: {
      fohTasks: boolean;
      setups: boolean;
      kitchen: boolean;
      documentation: boolean;
      training: boolean;
      evaluations: boolean;
      leadership: boolean;
      leadershipPlans: boolean;
      [key: string]: boolean;
    };
    stripe?: {
      subscriptionId?: string;
      customerId?: string;
      billingCycle?: 'monthly' | 'yearly';
    };
  };
  // Multi-store access
  multiStoreEnabled?: boolean;
  linkedStores?: Array<{ _id: string; name: string; storeNumber: string }> | string[];
}

export interface StoreInitializationStatus {
  storeId: string;
  hasDefaultGradingScale: boolean;
  activeTemplateCount: number;
  isInitialized: boolean;
}


export interface StoreUser {
  _id: string;
  name: string;
  email: string;
  position: string;
  departments: string[];
  isAdmin: boolean;
  role: 'admin' | 'user';
  status: 'active' | 'inactive';
  createdAt: string;
}

export interface StoreUsersResponse {
  store: {
    _id: string;
    storeNumber: string;
    name: string;
  };
  admins: StoreUser[];
  users: StoreUser[];
  totalUsers: number;
}

export interface NewStoreData {
  storeNumber: string;
  name: string;
  storeAddress: string;
  storePhone?: string;
  adminEmail: string;
  adminName: string;
  copyFromStoreId?: string; // Optional store ID to copy content from
}

export interface UpdateStoreData {
  storeNumber?: string;
  name?: string;
  storeAddress?: string;
  storePhone?: string;
  storeEmail?: string;
  createdAt?: string;
  billingMethod?: 'stripe' | 'check' | 'comp';
}

export interface NewUserData {
  name: string;
  email: string;
  position: string;
  departments: string[];
  isAdmin?: boolean;
  generatePassword?: boolean;
  password?: string;
}

export interface SearchedUser {
  _id: string;
  name: string;
  email: string;
  position: string;
  departments: string[];
  isAdmin: boolean;
  role: 'admin' | 'user';
  status: 'active' | 'inactive';
  createdAt: string;
  lastLogin?: string;
  store: {
    _id: string;
    storeNumber: string;
    name: string;
    storeAddress: string;
    storeEmail?: string;
    adminUsers?: {
      _id: string;
      name: string;
      email: string;
    }[];
  };
  manager?: {
    _id: string;
    name: string;
    email: string;
  };
}

export interface UserSearchResponse {
  users: SearchedUser[];
  totalFound: number;
  searchQuery: string;
  searchType: 'email' | 'name' | 'all';
}

export interface SubscriptionFeatures {
  fohTasks: boolean;
  setups: boolean;
  kitchen: boolean;
  documentation: boolean;
  training: boolean;
  evaluations: boolean;
  leadership: boolean;
  leadershipPlans: boolean;
  [key: string]: boolean;
}

export interface PendingChanges {
  hasChanges: boolean;
  features: SubscriptionFeatures;
  effectiveDate: string;
  submittedAt: string;
}

export interface SubscriptionPricing {
  sectionPrice: number;
  maxPrice: number;
}

export interface StoreSubscription {
  _id: string;
  store: string;
  subscriptionStatus: 'active' | 'expired' | 'trial' | 'none';
  features: SubscriptionFeatures;
  pricing: SubscriptionPricing;
  pendingChanges?: PendingChanges;
  currentPeriod?: {
    startDate: string;
    endDate: string;
  };
  stripe?: {
    subscriptionId?: string;
    customerId?: string;
    billingCycle?: 'monthly' | 'yearly';
  };
  calculatedCost?: number;
  pendingCost?: number;
}

const adminService = {
  /**
   * Get all stores in the system
   */
  getAllStores: async (): Promise<Store[]> => {
    const response = await api.get('/api/admin/stores');
    return response.data.stores;
  },

  /**
   * Add a new store to the system
   */
  addStore: async (storeData: NewStoreData) => {
    const response = await api.post('/api/admin/stores', storeData);
    return response.data;
  },

  /**
   * Update store details
   */
  updateStore: async (storeId: string, storeData: UpdateStoreData) => {
    const response = await api.put(`/api/admin/stores/${storeId}`, storeData);
    return response.data;
  },

  /**
   * Get store notes
   */
  getStoreNotes: async (storeId: string) => {
    const response = await api.get(`/api/admin/stores/${storeId}/notes`);
    return response.data;
  },

  /**
   * Add a note to a store
   */
  addStoreNote: async (storeId: string, content: string) => {
    const response = await api.post(`/api/admin/stores/${storeId}/notes`, { content });
    return response.data;
  },

  /**
   * Delete a note from a store
   */
  deleteStoreNote: async (storeId: string, noteIndex: number) => {
    const response = await api.delete(`/api/admin/stores/${storeId}/notes/${noteIndex}`);
    return response.data;
  },

  /**
   * Update store status (active/inactive)
   */
  updateStoreStatus: async (storeId: string, status: 'active' | 'inactive') => {
    const response = await api.put('/api/admin/stores/status', { storeId, status });
    return response.data;
  },

  /**
   * Get store users and admins
   */
  getStoreUsers: async (storeId: string): Promise<StoreUsersResponse> => {
    const response = await api.get(`/api/admin/stores/${storeId}/users`);
    return response.data;
  },

  /**
   * Add a user to a store
   */
  addStoreUser: async (storeId: string, userData: NewUserData) => {
    const response = await api.post(`/api/admin/stores/${storeId}/users`, userData);
    return response.data;
  },

  /**
   * Delete a user from a store
   */
  deleteStoreUser: async (storeId: string, userId: string) => {
    const response = await api.delete(`/api/admin/stores/${storeId}/users/${userId}`);
    return response.data;
  },

  /**
   * Update user email
   */
  updateUserEmail: async (storeId: string, userId: string, email: string) => {
    const response = await api.put(`/api/admin/stores/${storeId}/users/${userId}/email`, { email });
    return response.data;
  },

  /**
   * Reset user password
   */
  resetUserPassword: async (storeId: string, userId: string) => {
    const response = await api.post(`/api/admin/stores/${storeId}/users/${userId}/reset-password`);
    return response.data;
  },

  /**
   * Update store subscription status (active/expired/trial/none)
   */
  updateStoreSubscriptionStatus: async (storeId: string, subscriptionStatus: 'active' | 'expired' | 'trial' | 'none') => {
    const response = await api.put('/api/admin/stores/subscription-status', { storeId, subscriptionStatus })
    return response.data
  },

  /**
   * Get store subscription details
   */
  getStoreSubscription: async (storeId: string): Promise<{
    store: { _id: string; storeNumber: string; name: string };
    subscription: StoreSubscription;
    calculatedCost: number;
    pendingCost?: number;
  }> => {
    const response = await api.get(`/api/admin/stores/${storeId}/subscription`);
    return response.data;
  },

  /**
   * Update store subscription features
   */
  updateStoreSubscriptionFeatures: async (
    storeId: string,
    features: SubscriptionFeatures,
    applyImmediately: boolean = false
  ): Promise<StoreSubscription> => {
    const response = await api.put(`/api/admin/stores/${storeId}/subscription/features`, {
      features,
      applyImmediately
    });
    return response.data;
  },

  /**
   * Update store subscription billing cycle
   */
  updateStoreBillingCycle: async (storeId: string, billingCycle: 'monthly' | 'yearly') => {
    const response = await api.put(`/api/admin/stores/${storeId}/subscription/billing-cycle`, {
      storeId,
      billingCycle
    });
    return response.data;
  },

  /**
   * Get initialization status for a store
   */
  getStoreInitializationStatus: async (storeId: string): Promise<StoreInitializationStatus> => {
    const response = await api.get(`/api/admin/stores/${storeId}/init-status`);
    return response.data;
  },

  /**
   * Backfill store initialization (grading scale + evaluation templates)
   */
  backfillStoreInitialization: async (storeId: string): Promise<{
    message: string;
    result: any;
    status: StoreInitializationStatus;
  }> => {
    const response = await api.post(`/api/admin/stores/${storeId}/init-backfill`);
    return response.data;
  },

  /**
   * Get initialization status for all stores (batched)
   */
  getAllStoresInitializationStatus: async (): Promise<Record<string, StoreInitializationStatus>> => {
    const response = await api.get('/api/admin/stores/init-status/all');
    return response.data;
  },

  /**
   * Search users across all stores
   */
  searchUsers: async (query: string, searchType: 'email' | 'name' | 'all' = 'email'): Promise<UserSearchResponse> => {
    const response = await api.get('/api/admin/search-users', {
      params: { query, searchType }
    });
    return response.data;
  },

  /**
   * Clone configuration/templates from a source store into a target store (merge-only)
   */
  cloneFromStore: async (
    targetId: string,
    payload: {
      sourceId: string;
      include?: {
        foodSafety?: boolean;
        fohTasks?: boolean;
        kitchenShift?: boolean; // currently NOP
        evaluations?: boolean;
        training?: boolean;
        assessments?: boolean;
      };
      includeInactive?: boolean;
    }
  ): Promise<{ message: string; summary: any }> => {
    const response = await api.post(`/api/admin/stores/${targetId}/clone-from`, payload);
    return response.data;
  },

  // Follow-ups management
  addFollowUp: async (storeId: string, followUp: {
    title: string;
    description?: string;
    dueDate: string;
    priority: 'low' | 'medium' | 'high';
  }): Promise<FollowUp> => {
    const response = await api.post(`/api/admin/stores/${storeId}/follow-ups`, followUp);
    return response.data;
  },

  getStoreFollowUps: async (storeId: string): Promise<FollowUp[]> => {
    const response = await api.get(`/api/admin/stores/${storeId}/follow-ups`);
    return response.data;
  },

  completeFollowUp: async (storeId: string, followUpId: string): Promise<FollowUp> => {
    const response = await api.patch(`/api/admin/stores/${storeId}/follow-ups/${followUpId}/complete`);
    return response.data;
  },

  deleteFollowUp: async (storeId: string, followUpId: string): Promise<void> => {
    await api.delete(`/api/admin/stores/${storeId}/follow-ups/${followUpId}`);
  },

  updateFollowUp: async (storeId: string, followUpId: string, updates: {
    title?: string;
    description?: string;
    dueDate?: string;
    priority?: 'low' | 'medium' | 'high';
  }): Promise<FollowUp> => {
    const response = await api.patch(`/api/admin/stores/${storeId}/follow-ups/${followUpId}`, updates);
    return response.data;
  },

  // Multi-Store Management

  /**
   * Get multi-store configuration for a store
   */
  getMultiStoreConfig: async (storeId: string): Promise<{
    store: {
      _id: string;
      name: string;
      storeNumber: string;
      multiStoreEnabled: boolean;
      linkedStores: Array<{ _id: string; name: string; storeNumber: string }>;
    };
    multiStoreUsers: Array<{
      _id: string;
      name: string;
      email: string;
      position: string;
      store: { _id: string; name: string; storeNumber: string };
      authorizedStores: Array<{ _id: string; name: string; storeNumber: string }>;
    }>;
  }> => {
    const response = await api.get(`/api/admin/stores/${storeId}/multi-store`);
    return response.data;
  },

  /**
   * Enable multi-store access and link stores together
   */
  enableMultiStore: async (storeId: string, linkedStoreIds: string[]): Promise<{
    message: string;
    store: { _id: string; multiStoreEnabled: boolean; linkedStores: string[] };
  }> => {
    const response = await api.post(`/api/admin/stores/${storeId}/multi-store/enable`, {
      linkedStoreIds
    });
    return response.data;
  },

  /**
   * Disable multi-store access for a store
   */
  disableMultiStore: async (storeId: string): Promise<{
    message: string;
    store: { _id: string; multiStoreEnabled: boolean };
  }> => {
    const response = await api.post(`/api/admin/stores/${storeId}/multi-store/disable`);
    return response.data;
  },

};

export default adminService;
